import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Sparkles, Star } from 'lucide-react';
import { API } from '../config';
import { normalizeInviteCode, storeInviteCode } from '../lib/inviteCode';
import { useAuth } from '../contexts/AuthContext';

const PLAY_BASE = 'https://play.google.com/store/apps/details?id=it.rvincente.app.twa';
const APP_STORE_URL = 'https://apps.apple.com/app/rvincente-pronostici-calcio/id6760314785';

const detectOs = () => {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  return 'desktop';
};

const playUrl = (code) =>
  `${PLAY_BASE}&referrer=${encodeURIComponent(`utm_source=influencer&utm_medium=link&utm_campaign=${code}&ref=${code}`)}`;

// Influencer tracking link: /inf/MARIO → records the click, stores the code, then the store
export default function InfluencerPage() {
  const { code: rawCode } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const code = normalizeInviteCode(rawCode);
  const os = useMemo(detectOs, []);
  const [name, setName] = useState('');
  const storeUrl = os === 'android' ? playUrl(code) : os === 'ios' ? APP_STORE_URL : null;

  useEffect(() => {
    if (!code) { navigate('/', { replace: true }); return undefined; }
    if (loading) return undefined;
    // Already registered: never attribute, never touch the stored code — just open the app/home
    if (user) { navigate('/', { replace: true }); return undefined; }
    let t;
    try { navigator.sendBeacon(`${API}/attribution/invite`, JSON.stringify({ code })); } catch { /* noop */ }
    axios.get(`${API}/influencers/${code}`).then((r) => {
      setName(r.data.name);
      storeInviteCode(code);
      // App installed (opened by the link) or computer → straight to sign-up with the code filled in
      if (Capacitor.isNativePlatform() || os === 'desktop') { navigate('/login?tab=register', { replace: true }); return; }
      // Android carries the code through the Play referrer: go straight on. iPhone stays so the code is noted.
      if (os === 'android') t = setTimeout(() => { window.location.href = storeUrl; }, 1500);
    }).catch(() => navigate('/', { replace: true }));
    return () => clearTimeout(t);
  }, [code, loading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center p-6" data-testid="influencer-page">
      <div className="w-full max-w-sm text-center">
        <div className="w-16 h-16 rounded-full bg-[#F5A623]/15 border border-[#F5A623]/40 flex items-center justify-center mx-auto mb-5">
          <Star className="w-8 h-8 text-[#F5A623]" />
        </div>
        <h1 className="text-3xl font-black text-white uppercase mb-2" style={{ fontFamily: 'Barlow Condensed' }} data-testid="influencer-title">
          {name ? `${name} ti consiglia RVINCENTE` : 'Scarica RVINCENTE'}
        </h1>
        <p className="text-sm text-gray-400 mb-5">Analisi IA su tutte le partite, probabili marcatori e cartellini. Gratis.</p>
        <div className="rounded-2xl border-2 border-dashed border-[#F5A623]/60 bg-[#F5A623]/10 p-4 mb-5" data-testid="influencer-code-reminder">
          <p className="text-xs text-gray-300 mb-1">Ricorda il codice da scrivere in registrazione</p>
          <p className="text-4xl font-black tracking-widest text-[#F5A623]" style={{ fontFamily: 'Barlow Condensed' }} data-testid="influencer-code">{code}</p>
        </div>
        {storeUrl ? (
          <a href={storeUrl} className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-[#1A1A1A] font-black flex items-center justify-center gap-2" data-testid="influencer-store-btn">
            <Sparkles className="w-5 h-5" /> SCARICA L'APP GRATIS
          </a>
        ) : (
          <div className="space-y-3">
            <a href={playUrl(code)} className="w-full h-12 rounded-2xl bg-white/10 text-white font-bold flex items-center justify-center" data-testid="influencer-play-btn">Google Play</a>
            <a href={APP_STORE_URL} className="w-full h-12 rounded-2xl bg-white/10 text-white font-bold flex items-center justify-center" data-testid="influencer-appstore-btn">App Store</a>
          </div>
        )}
        <button onClick={() => navigate('/login?tab=register')} className="mt-4 text-sm text-gray-300 underline" data-testid="influencer-web-btn">
          Oppure registrati dal browser
        </button>
      </div>
    </div>
  );
}
